import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PARTNER_TYPES, PARTNER_PIPELINE, ACTIVE_STATUSES, AGREEMENT_STATUSES,
  COUNTRIES, TONE, optOf, labelOf, partnerTypeLabel,
} from "@/config/partnerOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Handshake, Plus, Search, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";

export const Pill = ({ arr, value, testid }) => {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

export const QualityStars = ({ value }) => {
  const n = Math.round(Number(value) || 0);
  if (!n) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={13} className={i <= n ? "fill-[#DEE029] text-[#B7791F]" : "text-[#E6EDF2]"} />
      ))}
    </span>
  );
};

const emptyNew = {
  company_name: "", partner_type: "", contact_name: "", phone: "", email: "", website: "",
  city: "", state_province: "", country: "", service_area: "", partner_status: "prospect",
  agreement_status: "none", referral_terms: "", quality_rating: "", branch_id: "", notes: "",
};

export default function Partners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("partners");

  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", partner_type: "all", partner_status: "all", active_status: "all", branch_id: "all" });
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyNew);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      ["partner_type", "partner_status", "active_status", "branch_id"].forEach((k) => {
        if (filters[k] && filters[k] !== "all") params[k] = filters[k];
      });
      const { data } = await api.get("/partners", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm({ ...emptyNew, branch_id: branches.length === 1 ? branches[0].id : "" }); setNewOpen(true); };
  const create = async (e) => {
    e.preventDefault();
    if (!form.company_name) { toast.error("Company name is required"); return; }
    setCreating(true);
    try {
      const payload = { ...form, quality_rating: form.quality_rating ? Number(form.quality_rating) : undefined };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === undefined) && delete payload[k]);
      const { data } = await api.post("/partners", payload);
      toast.success("Partner added");
      setNewOpen(false);
      navigate(`/partners/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add partner"); } finally { setCreating(false); }
  };

  const FilterSelect = ({ value, onChange, options, testid, all }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[150px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-[300px]"><SelectItem value="all">{all}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><Handshake size={22} className="text-[#1C4358]" /> Partners & Vendors</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Your referral network — communities, contractors, investors, and vendors who help our families.</p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid="new-partner-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={17} className="mr-1.5" /> New Partner</Button>}
      </div>

      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search company, contact, email..." className="pl-9 h-9" data-testid="partner-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex gap-2">
            <FilterSelect value={filters.partner_type} onChange={(v) => setFilters((p) => ({ ...p, partner_type: v }))} options={PARTNER_TYPES} testid="filter-type" all="All types" />
            <FilterSelect value={filters.partner_status} onChange={(v) => setFilters((p) => ({ ...p, partner_status: v }))} options={PARTNER_PIPELINE} testid="filter-pipeline" all="All stages" />
            <FilterSelect value={filters.active_status} onChange={(v) => setFilters((p) => ({ ...p, active_status: v }))} options={ACTIVE_STATUSES} testid="filter-active" all="Active & inactive" />
            <FilterSelect value={filters.branch_id} onChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))} options={branches.map((b) => ({ value: b.id, label: b.name }))} testid="filter-branch" all="All branches" />
          </div>
        </div>
      </Card>

      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><Handshake size={26} className="text-[#1C4358]" /></div>
            <p className="font-medium text-[#252221]">No partners yet</p>
            <p className="text-sm text-muted-foreground">Add your first partner or vendor to start building the referral network.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="partners-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Pipeline</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Quality</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Active</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="hover:bg-[rgba(155,185,206,0.10)] cursor-pointer" data-testid="partner-row" onClick={() => navigate(`/partners/${p.id}`)}>
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{p.display_name}</div>
                      <div className="text-xs text-muted-foreground">{p.contact_name || p.email || p.phone || ""}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.partner_type ? partnerTypeLabel(p.partner_type) : "—"}</TableCell>
                    <TableCell><Pill arr={PARTNER_PIPELINE} value={p.partner_status} testid="partner-pipeline-badge" /></TableCell>
                    <TableCell><QualityStars value={p.quality_rating} /></TableCell>
                    <TableCell><Pill arr={ACTIVE_STATUSES} value={p.active_status} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/partners/${p.id}`); }} data-testid="open-partner-button">Open <ChevronRight size={15} className="ml-1" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{items.length} partner{items.length === 1 ? "" : "s"}</p>

      <PopoutDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        title="New Partner"
        subtitle="Partners & Vendors"
        icon={Handshake}
        maxWidthClass="max-w-2xl"
        testid="new-partner-dialog"
      >
        <form onSubmit={create} className="space-y-4">
          <p className="text-sm text-muted-foreground -mt-1">Add a partner or vendor to your referral network.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Company name *</Label><Input value={form.company_name} onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))} data-testid="partner-company-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Partner type</Label>
                <Select value={form.partner_type} onValueChange={(v) => setForm((p) => ({ ...p, partner_type: v }))}>
                  <SelectTrigger data-testid="partner-type-new"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">{PARTNER_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Contact person</Label><Input value={form.contact_name} onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))} data-testid="partner-contact-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Website</Label><Input value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Service area</Label><Input value={form.service_area} onChange={(e) => setForm((p) => ({ ...p, service_area: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">City</Label><Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">State / province</Label><Input value={form.state_province} onChange={(e) => setForm((p) => ({ ...p, state_province: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm((p) => ({ ...p, country: v }))}>
                  <SelectTrigger data-testid="partner-country-new"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Pipeline stage</Label>
                <Select value={form.partner_status} onValueChange={(v) => setForm((p) => ({ ...p, partner_status: v }))}>
                  <SelectTrigger data-testid="partner-pipeline-new"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_PIPELINE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Agreement status</Label>
                <Select value={form.agreement_status} onValueChange={(v) => setForm((p) => ({ ...p, agreement_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{AGREEMENT_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {branches.length > 1 && (
                <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Assigned branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm((p) => ({ ...p, branch_id: v }))}>
                    <SelectTrigger data-testid="partner-branch-new"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Commission / referral terms</Label><Input value={form.referral_terms} onChange={(e) => setForm((p) => ({ ...p, referral_terms: e.target.value }))} placeholder="e.g. 10% referral fee" data-testid="partner-terms-new" /></div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="partner-create-button">{creating ? "Adding..." : "Add Partner"}</Button>
            </div>
        </form>
      </PopoutDialog>
    </div>
  );
}
